import React from 'react';
import { T, FONT_STACK, NUMERIC, money } from '../customer/customers.theme';
import {
  CloseIcon,
  EditIcon,
  Spinner,
  TrashIcon,
} from '../customer/CustomersUI';
import { Thumbnail } from '../menu/MenuUI';
import { initialsOf } from '../menu/menuApi';
import { dayLabel, timeLabel } from '../../utils/appDate';
import { COPY } from './stock.copy';
import { movementTone } from './StockIcons';
import { statusOf } from './StockList';

/**
 * One item, and where it all went.
 *
 * `GET /stocks/:id/history` answers this, and it answers it properly: the
 * server walks the posted movements in date order and hands back each line
 * with the running `balanceAfter`, which is the one column a client must not
 * try to compute for itself — a movement this shop cannot see (a date range,
 * someone else's edit) would make the running total wrong in a way nobody
 * would notice.
 *
 * Opened from the list on the left. A movement number in here jumps to that
 * document in the feed.
 */
export default function StockHistoryDialog({
  item,
  summary,
  history,
  loading,
  currency,
  canWrite,
  onClose,
  onEdit,
  onDelete,
  onOpenMovement,
}) {
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!item) return null;

  const state = statusOf(item);

  const stat = (label, value, colour) => (
    <div style={{ flex: 1, padding: '10px 24px', borderRight: `1px solid ${T.hairline}` }}>
      <div style={{ fontSize: 12, color: T.inkFaint }}>{label}</div>
      <b style={{ fontSize: 17, color: colour || T.ink, ...NUMERIC }}>{value}</b>
    </div>
  );

  return (
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: T.backdrop,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        style={{
          width: 820,
          maxWidth: '100%',
          maxHeight: '86vh',
          display: 'flex',
          flexDirection: 'column',
          background: T.surface,
          borderRadius: T.radiusDialog,
          boxShadow: T.dialog,
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px 16px' }}>
          <span style={{ width: 60, flexShrink: 0 }}>
            <Thumbnail src={item.imageUrl} initials={initialsOf(item.name)} height={60} radius={10} />
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 19, fontWeight: 700 }}>
              {item.name}
              {state === 'out' ? (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: T.radiusPill,
                    background: T.dangerTint,
                    color: T.danger,
                    border: `1px solid ${T.dangerBorder}`,
                  }}
                >
                  {COPY.outOfStock}
                </span>
              ) : state === 'low' ? (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: T.radiusPill,
                    background: T.pendingTint,
                    color: T.pendingInk,
                    border: `1px solid ${T.pendingBorder}`,
                  }}
                >
                  {COPY.lowBadge}
                </span>
              ) : null}
            </div>
            <div style={{ fontSize: 12.5, color: T.inkFaint, marginTop: 4, ...NUMERIC }}>
              {money(item.cost, currency)} per {item.unit}
              {Number(item.reorderLevel || 0) > 0
                ? ` · low at ${item.reorderLevel} ${item.unit}`
                : ''}
            </div>
          </div>

          {canWrite ? (
            <React.Fragment>
              <button
                type="button"
                onClick={() => onEdit(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 14px',
                  border: `1px solid ${T.borderStrong}`,
                  borderRadius: T.radiusControl,
                  background: T.surface,
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.ink,
                  cursor: 'pointer',
                }}
              >
                <EditIcon />
                {COPY.edit}
              </button>

              <button
                type="button"
                title={COPY.removeItem}
                onClick={() => onDelete(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  border: `1px solid ${T.borderStrong}`,
                  borderRadius: T.radiusControl,
                  background: T.surface,
                  color: T.danger,
                  cursor: 'pointer',
                }}
              >
                <TrashIcon />
              </button>
            </React.Fragment>
          ) : null}

          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              border: 'none',
              borderRadius: T.radiusControl,
              background: 'transparent',
              color: T.inkFaint,
              cursor: 'pointer',
            }}
          >
            <CloseIcon />
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            borderTop: `1px solid ${T.hairline}`,
            borderBottom: `1px solid ${T.hairline}`,
          }}
        >
          {stat(
            COPY.itemOnHand,
            `${summary ? summary.onHand : item.quantity} ${item.unit}`,
            state === 'out' ? T.danger : state === 'low' ? T.pendingInk : T.ink
          )}
          {stat(COPY.itemValue, money(summary?.value, currency))}
          {stat(COPY.itemReceived, `+${summary?.received || 0}`, '#15803D')}
          {stat(
            COPY.itemUsedWasted,
            `−${(Number(summary?.used || 0) + Number(summary?.wasted || 0))}`,
            T.brandInk
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 20px' }}>
          <div
            style={{
              padding: '16px 0 8px',
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: T.inkFaint,
            }}
          >
            {COPY.historyTitle}
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 30, color: T.inkFaint }}>
              <Spinner size={20} />
            </div>
          ) : null}

          {!loading && !history.length ? (
            <div style={{ padding: '30px 0', textAlign: 'center', fontSize: 13.5, color: T.inkSoft }}>
              {COPY.historyEmpty}
            </div>
          ) : null}

          {history.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    { label: 'Date', align: 'left' },
                    { label: 'Movement', align: 'left' },
                    { label: 'Reason', align: 'left' },
                    { label: 'Qty', align: 'right' },
                    { label: 'Value', align: 'right' },
                    { label: COPY.leftAfter, align: 'right' },
                  ].map((column) => (
                    <th
                      key={column.label}
                      style={{
                        position: 'sticky',
                        top: 0,
                        padding: '10px 8px 8px',
                        textAlign: column.align,
                        background: T.surface,
                        borderBottom: `1px solid ${T.border}`,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: T.inkFaint,
                      }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {history.map((row, position) => {
                  const tone = movementTone({ type: row.type, reason: row.reason });

                  return (
                    <tr key={`${row.movementId}-${position}`}>
                      <td
                        style={{
                          padding: '9px 8px',
                          borderBottom: `1px solid ${T.hairline}`,
                          boxShadow: `inset 3px 0 0 ${tone.solid}`,
                          ...NUMERIC,
                        }}
                      >
                        {dayLabel(row.date)}{' '}
                        <span style={{ color: T.inkFaint }}>{timeLabel(row.date)}</span>
                      </td>

                      <td style={{ padding: '9px 8px', borderBottom: `1px solid ${T.hairline}` }}>
                        <span
                          onClick={() => onOpenMovement(row.movementId)}
                          style={{ color: T.brandInk, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {row.no}
                        </span>
                      </td>

                      <td style={{ padding: '9px 8px', borderBottom: `1px solid ${T.hairline}` }}>
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: T.radiusPill,
                            background: tone.tint,
                            border: `1px solid ${tone.border}`,
                            color: tone.ink,
                          }}
                        >
                          {row.reason}
                        </span>{' '}
                        <span style={{ fontSize: 12, color: T.inkFaint }}>
                          {row.vendor?.name || row.station?.name || row.note || ''}
                        </span>
                      </td>

                      <td
                        style={{
                          padding: '9px 8px',
                          borderBottom: `1px solid ${T.hairline}`,
                          textAlign: 'right',
                          fontWeight: 700,
                          color: tone.ink,
                          ...NUMERIC,
                        }}
                      >
                        {row.type === 'in' ? '+' : '−'}
                        {row.qty}
                      </td>

                      <td
                        style={{
                          padding: '9px 8px',
                          borderBottom: `1px solid ${T.hairline}`,
                          textAlign: 'right',
                          color: T.inkSoft,
                          ...NUMERIC,
                        }}
                      >
                        {money(row.subtotal, currency)}
                      </td>

                      <td
                        style={{
                          padding: '9px 8px',
                          borderBottom: `1px solid ${T.hairline}`,
                          textAlign: 'right',
                          ...NUMERIC,
                        }}
                      >
                        <b>{row.balanceAfter}</b>{' '}
                        <span style={{ color: T.inkFaint }}>{item.unit}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </div>
  );
}
